import { useTranslation } from 'react-i18next'
import { aiTag, buyingPowerLevel, temperatureLevel, type RecencyTier } from './aiLayer'

/** "$125.5 spent" (chats.js renderChatSpentBadge). */
export function SpentBadge({ amount }: { amount: number }) {
  const { t } = useTranslation('chats')
  const formatted = amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
  return (
    <span className="chat-spent-badge" title={t('spentTitle', { amount: formatted })}>
      {t('spent', { amount: formatted })}
    </span>
  )
}

/** Five dots, filled to the fan's buying power. */
export function BuyingPowerDots({ value, known }: { value: unknown; known: boolean }) {
  const { t } = useTranslation('chats')
  const level = known && value != null ? buyingPowerLevel(value) : 0
  const state = !known ? 'loading' : value == null ? 'unavailable' : 'fresh'
  const label = !known ? t('bpLoading') : value == null ? t('bpUnavailable') : t('bpValue', { value: String(value) })
  return (
    <span className={`chat-buying-power-dots chat-buying-power-dots--compact chat-buying-power-dots--${state}`} role="img" title={label} aria-label={label}>
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} className={`chat-buying-power-dot ${i < level ? 'is-filled' : ''}`} />
      ))}
    </span>
  )
}

/** OnlyMonster's temperature ring around the avatar (chat-temperature.js). */
const VIEWBOX = 72
const CENTER = VIEWBOX / 2
const RADIUS = 31.5
const STROKE = 4.5
const START = 120
const ARC = 120
const GAP = 5
const CIRC = 2 * Math.PI * RADIUS
const SEGMENT = (ARC - GAP * 3) / 4
const STEP = SEGMENT + GAP
const DASH = (SEGMENT / 360) * CIRC

function pointOnRing(deg: number) {
  const rad = (deg * Math.PI) / 180
  return { x: CENTER + RADIUS * Math.cos(rad), y: CENTER + RADIUS * Math.sin(rad) }
}

export function TemperatureRing({ value }: { value: number | null | undefined }) {
  const { t } = useTranslation('chats')
  const temp = temperatureLevel(value)
  const label = temp ? t('temperature', { score: temp.score, level: temp.level }) : t('temperatureNone')
  const state = (lvl: number) => (temp && lvl <= temp.level ? ` is-filled temperature-level-${temp.level}` : '')
  return (
    <span className="chat-temperature-ring" role="img" title={label} aria-label={label}>
      <svg className="chat-temperature-ring-svg" viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`} focusable="false" aria-hidden="true">
        {[1, 2, 3, 4].map((lvl, i) => (
          <circle
            key={lvl}
            className={`chat-temperature-segment${state(lvl)}`}
            cx={CENTER}
            cy={CENTER}
            r={RADIUS}
            strokeWidth={STROKE}
            strokeDasharray={`${DASH} ${CIRC}`}
            transform={`rotate(${START + i * STEP} ${CENTER} ${CENTER})`}
          />
        ))}
        {[
          { lvl: 1, deg: START },
          { lvl: 4, deg: START + ARC },
        ].map(({ lvl, deg }) => {
          const p = pointOnRing(deg)
          return <circle key={lvl} className={`chat-temperature-cap${state(lvl)}`} cx={p.x} cy={p.y} r={STROKE / 2} />
        })}
      </svg>
    </span>
  )
}

/** The one AI tag: AI chats / teases / sexts / ignores / needs you. */
export function AiTagChip({ status, vibe }: { status: unknown; vibe: unknown }) {
  const { t } = useTranslation('chats')
  const tag = aiTag(status, vibe)
  if (!tag) return null
  return (
    <span className="chat-ai-badges">
      <span className={`chat-ai-tag chat-ai-tag--${tag}`}>{t(`aiTag.${tag}`)}</span>
    </span>
  )
}

export function RecencyDot({ tier }: { tier: RecencyTier }) {
  const { t } = useTranslation('chats')
  return <span className={`chat-ai-recency chat-ai-recency--${tier}`} title={t(`recency.${tier}`)} aria-hidden="true" />
}
